import axios from 'axios';
import CryptoJS from 'crypto-js';

// API Configuration
const API_CONFIG = {
  // Base URLs
  // TODO: Change this back to 'http://localhost:8000/api' for local development
  // or create a .env file with REACT_APP_API_BASE_URL=http://192.168.114.200:8000/api
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api',
  WS_BASE_URL: process.env.REACT_APP_WS_BASE_URL || 'ws://localhost:8000/ws',
  
  // Authentication endpoints
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    PROFILE: '/auth/profile',
    PASSWORD_CHANGE: '/auth/password/change/',
    USER: '/v1/auth/user/',
    CHECK_2FA: '/v1/check2fa/',
    ENABLE_2FA: '/v1/set2fa/',
    DISABLE_2FA: '/v1/remove2fa/'
  },
  
  // Trading endpoints
  TRADING: {
    ORDERS: '/v1/orders/',
    ORDERBOOK: '/orderbook',
    TRADES: '/trades',
    POSITIONS: '/positions',
    BALANCE: 'v1/balance',
    MARKET: '/v1/market/'
  },
  
  // Wallet endpoints
  WALLET: {
    BALANCE: '/wallet/balance',
    DEPOSIT: '/wallet/deposit',
    WITHDRAW: '/wallet/withdraw',
    TRANSACTIONS: '/wallet/transactions',
    ADDRESSES: '/wallet/addresses'
  },
  
  // P2P endpoints
  P2P: {
    ORDERS: '/p2p/orders',
    TRADES: '/p2p/trades',
    CHAT: '/p2p/chat',
    DISPUTES: '/p2p/disputes'
  },
  
  // Market data endpoints
  MARKET: {
    TICKERS: '/market/tickers',
    CANDLES: '/market/candles',
    // DEPTH: '/market/depth',
    PAIRS: '/market/pairs'
  },
  
  // User profile endpoints
  PROFILE: {
    SETTINGS: '/profile/settings',
    SECURITY: '/profile/security',
    VERIFICATION: '/profile/verification'
  },
  
  // Notifications endpoints
  NOTIFICATIONS: {
    LIST: '/notifications',
    MARK_READ: '/notifications/mark-read',
    SETTINGS: '/notifications/settings'
  },
  
  // Public API endpoints
  PUBLIC: {
    COINGECKO: '/public/coingecko',
    MARKET_DATA: '/public/market-data',
    EXCHANGE_INFO: '/public/exchange-info',
    COINS: '/v1/coins/'
  },
  
  // Fees endpoints
  FEES: {
    CRYPTO: '/v1/fees/{crypto}/'
  }
};

// Helper function to build full API URLs
export const buildApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to build WebSocket URLs
export const buildWsUrl = (endpoint) => {
  return `${API_CONFIG.WS_BASE_URL}${endpoint}`;
};

// API request configuration
export const API_REQUEST_CONFIG = {
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
};

// WebSocket configuration
export const WS_CONFIG = {
  reconnectInterval: 5000,
  maxReconnectAttempts: 5,
  heartbeatInterval: 30000
};

// HMAC Authentication for API calls
export class HMACAuth {
  constructor(apiKey, secretKey) {
    this.apiKey = apiKey;
    this.secretKey = secretKey;
  }

  generateSignature(nonce) {
    const message = this.apiKey + nonce;
    return CryptoJS.HmacSHA256(message, this.secretKey).toString().toUpperCase();
  }

  getHeaders() {
    const nonce = Date.now().toString();
    const signature = this.generateSignature(nonce);

    return {
      'APIKEY': this.apiKey,
      'SIGNATURE': signature,
      'NONCE': nonce,
    };
  }
}

// Create axios instances
export const publicApi = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const privateApi = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token interceptor for private API
privateApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for token refresh
privateApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_CONFIG.BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem('access_token', access);

          return privateApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Add response interceptor for public API
publicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Public API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Export API_CONFIG for backward compatibility
export { API_CONFIG };

export default API_CONFIG; 